import React from 'react';
import { find, isNil, cloneDeep, findIndex, get, isEmpty, isEqual } from 'lodash';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// local dependencies
import { authFlowCallerIdentificationPharmacy as AuthFlowCallerIdentificationPharmacy } from 'ui/views/widgets/authFlowCallerIdentificationPharmacy';
import { AuthFlowCallerIdentificationPharmacyWidgetService } from 'services/authFlowCallerIdentificationPharmacyWidget';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION, PAGE_LEVEL_PRELOADER_TEMPLATES, PHARMACY_ID_TYPE, CLINIC_NUMBER } from 'constants/common';
import { REDUCER } from 'constants/entity-names';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { updateCallState, initCallState } from 'store/actions/callState';
import { isInbound, getCallerTypeFromName } from 'resolvers/callStateEntities';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { AuthFlowCallerIdentificationPharmacyWidget } from 'ui/containers/AuthFlowCallerIdentificationPharmacyWidget';
 *
 * // render
 * <AuthFlowCallerIdentificationPharmacyWidget propName='propValue'/>
 */
export class _AuthFlowCallerIdentificationPharmacyWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods to instance
		this.__bind();

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
			mode: 'editable', // could be `readonly` or `editable`
			editItemId: null, // when an item is in edit mode
			isSearchPharmacyVisible: false, //search visibility
            isPharmacyVerificationNeeded: true,
			pharmacySearchType: null,
			npiNumber: null,
            clinicNumber: null,
            isCriteriaVisible: false,
            isMemberVerified: false,
            actionsForNext: null,
            dataForMatchCriteria: null
        };

        // create service instance
        this.authFlowCallerIdentificationPharmacyWidgetService = new AuthFlowCallerIdentificationPharmacyWidgetService();
        
        // start data fetch
        this.fetchComponentData();
    }

    // bind methods and events to instance
	__bind() {

		this.handleToggleEditMode = this.handleToggleEditMode.bind( this );
        this.handleFormSubmit = this.handleFormSubmit.bind( this );
		this.handlePharmacySearchTypeChange = this.handlePharmacySearchTypeChange.bind( this );
		this.onSelectPharmacySearch = this.onSelectPharmacySearch.bind( this );
		this.handlePharmacySearchQueryChange = this.handlePharmacySearchQueryChange.bind( this );
        this.handleActionClick = this.handleActionClick.bind( this );
        this.handleMarkVerifiedAndShowNext = this.handleMarkVerifiedAndShowNext.bind( this );
        this.saveCriteriaSelection = this.saveCriteriaSelection.bind( this );
        this.updateClinicNumber = this.updateClinicNumber.bind( this );
	}

	/*********************************************/
	

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'AuthFlowCallerIdentificationPharmacyWidget.fetchComponentData()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationPharmacyWidgetService.cancel();

        // fetch component data
        const { status, data } = await this.authFlowCallerIdentificationPharmacyWidgetService.fetchComponentData( {
            url: this.props.widget.url,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
	}

	/* ************************************************************* */

	/**
     * @desc Action handler for next button click.
     */
    handleActionClick( { item = {} } = {} ) {
        log.debug( 'AuthFlowCallerIdentificationPharmacyWidget.handleActionClick()'  );

        // get member id
        // const memberId = get( item, 'memberId', null );
        const firstName = get( item, 'firstName', '' );
        const lastName = get( item, 'lastName', '' );

        // extract actions
        const actions = get( this.state.dataForMatchCriteria, 'actions' );
        const clinicNumberActions = get( this.state.componentData, 'sections.callerVerification.actions' );

        // getting caller type value
        const callerType = getCallerTypeFromName( this.props.widget.value.callerType );

        /*---------*/

        // if it's outbound call, move to the next step
        if( ! isInbound() ) {
            this.props.onNextActionClick( { name: 'nextOutbound' } );
        }

        /*---------*/

        // if its inbound call, register an inbound caller
        if ( isInbound() && !this.state.isPharmacyVerificationNeeded ) {
            const action = find( clinicNumberActions, { name: 'nextInbound'} );
            return this.registerInboundCaller( {
                actionName: action.name,
                url: action.url,
                payload: {
                    id: this.state.clinicNumber,
                    firstName,
                    lastName,
                },
                callerType: callerType
            } );
        } else if( isInbound() ) {
            const action = find( actions, { name: 'nextInbound'} );

            return this.registerInboundCaller( {
                actionName: action.name,
                url: action.url,
                payload: {
                    id: this.state.npiNumber,
                    firstName,
                    lastName,
                },
                callerType: callerType
            } );
        }
    }

    /**
     * @desc Request backend to register `member` as the caller for the ongoing inbound call.
     */
    async registerInboundCaller( { actionName, url, payload, callerType } ) {
        log.debug( 'AuthFlowCallerIdentificationPharmacyWidget.registerInboundCaller()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationPharmacyWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // if pharmacySearchType exists, send it along with other request payload
        const additionalRequestData = get( this.state, 'pharmacySearchType', null ) ? { idType: this.state.pharmacySearchType } : {};

        // fetch component data
        const { status } = await this.authFlowCallerIdentificationPharmacyWidgetService.registerInboundCaller( {
            url: url,
            data: {
                ...payload,
                ...additionalRequestData
            },
            callerType: callerType,
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {

                // update call data in the store
                this.props.updateCallState( {
                    callerId: payload.id,
                    callerType: callerType,
                    callerInfo: {
                        id: null,
                        refId: null,
                        type: callerType,
                        firstName: null,
                        lastName: null,
                    },
                } );
                
                // move to the next step
                this.props.onNextActionClick( { name: actionName } );
            } );
        } else {
            errorOverlay.open(); // show error
        }

	}
	
    /************************************************************** */
    
    /**
     * @desc Mark member as verified (from criteria match widget).
     */
    handleMarkVerifiedAndShowNext( { isVerified = false } ) {
        log.debug( 'AuthFlowCallerIdentificationPharmacyWidget.handleMarkVerifiedAndShowNext()' );

        this.setState( {
            isMemberVerified: isVerified,
        } );
    }

    /************************************************************** */

    /**
	 * @desc Toggle between read and edit mode.
	 */
	handleToggleEditMode( editable = true, editItemId = null ) {
		log.debug( 'AuthFlowCallerIdentificationPharmacyWidget.handleToggleEditMode() ' );

		this.setState( {
			mode: editable ? 'editable' : 'readonly',
			editItemId, // when `edit` action is clicked
		} );
	}

    /**
	 * @desc Handle form submit.
	 */
	async handleFormSubmit( { memberId, title, firstName, lastName } = {} ) {
		log.debug( 'AuthFlowCallerIdentificationPharmacyWidget.handleFormSubmit()' );

		// get save item action
		const saveAction = find( this.state.componentData.sections.callerDetails.actions, { name: 'save' } );

		// cancel ongoing request
		this.authFlowCallerIdentificationPharmacyWidgetService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const { status, data } = await this.authFlowCallerIdentificationPharmacyWidgetService.addUpdateItem( {
			url: saveAction.url,
			data: {
				operation: isNil( memberId ) ? CRUD_OPERATION.CREATE : CRUD_OPERATION.UPDATE,
				title,
                firstName,
                lastName,
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

			// update or insert item based on `id`
			const items = cloneDeep( this.state.componentData.items );

			if ( isNil( memberId ) ) {
				items.push( data ); // push new item
			} else {
				const itemIndex = findIndex( items, { memberId } );
				items[ itemIndex ] = data; // update data
            }

            // showing pharmacy seacrhed already, if outbound the call is
            const callerInfo = get( get( this.props.callState, 'callerInfo', {} ), 'miscData', {} );
            const npi = get( callerInfo, 'npi', '' );
            const nabp = get( callerInfo, 'nabp', '' );

            const searchType = ! isEmpty( npi ) ? PHARMACY_ID_TYPE.NPI : PHARMACY_ID_TYPE.NABP ;

			/*----------*/

			this.setState( {
				isFetching: false,
				isSearchPharmacyVisible: isInbound() ? true : false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				mode: 'readonly',
                editItemId: null,
                npiNumber: ! isEmpty( npi ) ? npi : nabp,
                pharmacySearchType: ! isInbound() ? searchType : null,
				componentData: {
					...this.state.componentData,
					items, // update new items
				},
			}, () => {
                ! isEmpty( callerInfo ) ? this.onSelectPharmacySearch( this.state.npiNumber ) : null;
            } );
		} else {
			errorOverlay.open(); // show error
		}
    }

    /**
	 * @desc When pharmacy type changes, update value in the state
	 */
    async handlePharmacySearchTypeChange( selectedType ) {
        log.debug( 'AuthFlowCallerIdentificationPharmacyWidget.handlePharmacySearchTypeChange()' );
    
        if ( isEqual( selectedType, CLINIC_NUMBER ) ) {
    
            this.setState( {
                pharmacySearchType: selectedType,
                isPharmacyVerificationNeeded: false,
                clinicNumber: null,
            } );
    
        } else if ( ! this.state.isPharmacyVerificationNeeded && !isEqual( selectedType, CLINIC_NUMBER ) ) {
    
            this.setState( {
                isMemberVerified: false,
                pharmacySearchType: selectedType,
                isPharmacyVerificationNeeded: true,
                isCriteriaVisible: false
            } );
    
        } else {
            this.setState( {
                pharmacySearchType: selectedType,
                isPharmacyVerificationNeeded: true,
            } );
        }
    }

    /**
	 * @desc fetch match criteria for the pharmacy
	 */
	async onSelectPharmacySearch( searchVal ) {
		log.debug( 'AuthFlowCallerIdentificationPharmacyWidget.onSelectPharmacySearch()' );

		// cancel ongoing request
		this.authFlowCallerIdentificationPharmacyWidgetService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const { status, data } = await this.authFlowCallerIdentificationPharmacyWidgetService.fetchMatchCriteria( {
			url: this.state.componentData.sections.callerVerification.url,
			data: {
                id: searchVal,
                idType: this.state.pharmacySearchType,
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				npiNumber: searchVal,
                isCriteriaVisible: true,
                dataForMatchCriteria: data,
                isMemberVerified: false,
			} );
		} else {
			errorOverlay.open(); // show error
		}
    }

    /**
     * @desc When pharmacy search query change, hide criteria view
     */
    handlePharmacySearchQueryChange() {
		log.debug( 'AuthFlowCallerIdentificationPharmacyWidget.handlePharmacySearchQueryChange()' );

        if( this.state.isCriteriaVisible ) {
            this.setState( {
                isCriteriaVisible: false,
                isMemberVerified: false
            } );
        }
    }

    x/**
     * @desc When pharmacy clinic number is entered, edit criteria
     */
    updateClinicNumber( { value } ) {
		log.debug( 'AuthFlowCallerIdentificationPharmacyWidget.updateClinicNumber()' );

        this.setState( {
            clinicNumber: value,
        }, () => {
            ( 0 < this.state.clinicNumber.length ? this.setState( { isMemberVerified: true } ) : this.setState( { isMemberVerified: false } ) );
        } );
    }

    /**
     * @desc Update criteria selection on the backend.
     */
    async saveCriteriaSelection( { saveAction, fieldStatus } ) {

        log.debug( 'AuthFlowCallerIdentificationPharmacyWidget.saveCriteriaSelection()' );

        if( isEmpty( saveAction ) ) {
            return;
        }

        /*----------*/

        // cancel ongoing request
        this.authFlowCallerIdentificationPharmacyWidgetService.cancel();

        // show page level preloader
        pageLevelPreloader.show( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );

        // get status and service data
        const { status } = await this.authFlowCallerIdentificationPharmacyWidgetService.saveCriteriaSelection( {
            url: saveAction.url,
            data: {
                id: this.state.npiNumber,
                idType: this.state.pharmacySearchType,

                // ...pickBy( fieldStatus, ( { value } = {} ) => ! isNil( value ) ),
                ...fieldStatus
            },
        } );

        // hide page level preloader
        pageLevelPreloader.hide( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {
            return null; // ignore on request cancellations
         } else if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
    */
    componentWillUnmount() {
        this.authFlowCallerIdentificationPharmacyWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {

        // getting caller type value
        const callerType = getCallerTypeFromName( this.props.widget.value.callerType );


        return (
            <AuthFlowCallerIdentificationPharmacy
                className={ this.props.className }
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                hasCallStarted={ ! isEmpty( this.props.callState ) }
                componentData={ get( this.state, 'componentData', {} ) }
                onFormSubmit={ this.handleFormSubmit }
                onToggleEditMode={ this.handleToggleEditMode }
                mode={ this.state.mode }
                isPharmacyVerificationNeeded={ this.state.isPharmacyVerificationNeeded }
                updateClinicNumber= { this.updateClinicNumber }
                clinicNumber= { this.state.clinicNumber }
				pharmacySearchType={ this.state.pharmacySearchType }
				onPharmacySearchTypeChange={ this.handlePharmacySearchTypeChange }
				onSelectPharmacySearch={ this.onSelectPharmacySearch }
                onPharmacySearchQueryChange={ this.handlePharmacySearchQueryChange }
				isSearchPharmacyVisible={ this.state.isSearchPharmacyVisible }
				isCriteriaVisible={ this.state.isCriteriaVisible }
				npiNumber={ this.state.npiNumber }
				editItemId={ this.state.editItemId }
                handleActionClick={ this.handleActionClick }
                handleMarkVerifiedAndShowNext={ this.handleMarkVerifiedAndShowNext }
                isMemberVerified={ this.state.isMemberVerified }
                actionsForNext={ this.state.actionsForNext }
                dataForMatchCriteria={ this.state.dataForMatchCriteria }
                saveCriteriaSelection={ this.saveCriteriaSelection }
                callerType={ callerType }
                displayBtnCncl={ get( this.state, 'componentData.sections.callerDetails.actions[1]', {} ) }
            />
        );
    }
}

// set display name
_AuthFlowCallerIdentificationPharmacyWidget.displayName = 'AuthFlowCallerIdentificationPharmacyWidget';

// set default props
_AuthFlowCallerIdentificationPharmacyWidget.defaultProps = {};

// prop types
_AuthFlowCallerIdentificationPharmacyWidget.propTypes = {};

/************************************************************ */

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),
        updateCallState: bindActionCreators( updateCallState, dispatch ),
    };
};

// return component with acccess to Redux store
export const AuthFlowCallerIdentificationPharmacyWidget = withRouter( connect( mapStateToProps, mapDispatchToProps )( _AuthFlowCallerIdentificationPharmacyWidget ) );
